// Every address the app answers to (05-routes-links.md): the route table, what a channel or space
// part must look like, desyr's own links, and the return path after sign-in. Pure functions; the
// app lives at "/" (the hash router and base path were candidates to drop, not built).
import { readJson, writeJson } from './storage';

export type Route =
  | { page: 'root' }
  | { page: 'login'; server?: string }
  | { page: 'legal'; doc: string }
  | { page: 'setup' | 'recover'; server?: string }
  | { page: 'home' | 'home-create' | 'home-search' | 'home-saved' | 'home-mentions' | 'home-invites' }
  | { page: 'home-room'; room: string; event?: string }
  | { page: 'direct' | 'direct-create' }
  | { page: 'direct-room'; room: string; event?: string }
  | { page: 'space' | 'space-lobby' | 'space-search'; space: string }
  | { page: 'space-room'; space: string; room: string; event?: string }
  | { page: 'create' }
  /** The phone's Activity and You tabs (07, v1.58.31); a computer opens Mentions and Settings. */
  | { page: 'activity' | 'you' }
  | { page: 'module'; module: 'calendar' | 'files'; rest: string[] }
  | { page: 'to' }
  | { page: 'not-found' };

/** Routes that open without a session (05: "Signed out or signed in" and "Signed out only"). */
export const SIGNED_OUT_PAGES: Route['page'][] = ['root', 'login', 'legal', 'setup', 'recover'];

const SERVER = /^(\d{1,3}(\.\d{1,3}){3}|\[[0-9a-fA-F:.]+\]|[A-Za-z0-9-]{1,63}(\.[A-Za-z0-9-]{1,63})*)(:\d{1,5})?$/;

/** A channel address "#name:server", a channel ID "!opaque:server", or a newest-version ID. */
export function isRoomRef(ref: string): boolean {
  if (!ref || ref.length > 255) return false;
  if (/^![A-Za-z0-9_-]{43}$/.test(ref)) return true;
  const colon = ref.indexOf(':');
  if (colon < 2 || !'#!'.includes(ref[0])) return false;
  const local = ref.slice(1, colon);
  if (ref[0] === '#' && /[\s:/]/.test(local)) return false;
  return SERVER.test(ref.slice(colon + 1));
}

const decode = (part: string) => {
  try {
    return decodeURIComponent(part);
  } catch {
    return part; // a broken % sequence is read as it stands
  }
};

/** The page for an app path (pathname only; the query and fragment stay with the page). */
export function parseRoute(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean).map(decode);
  const [first, second, third, fourth, ...more] = parts;
  const room = (ref: string | undefined) => ref !== undefined && isRoomRef(ref);
  if (!first) return { page: 'root' };
  switch (first) {
    case 'login':
      return parts.length <= 2 ? { page: 'login', server: second } : { page: 'not-found' };
    case 'legal':
      return parts.length === 2 ? { page: 'legal', doc: second } : { page: 'not-found' };
    case 'setup':
    case 'recover':
      return parts.length <= 2 ? { page: first, server: second } : { page: 'not-found' };
    case 'create':
      return parts.length === 1 ? { page: 'create' } : { page: 'not-found' };
    case 'to':
    case 'activity':
    case 'you':
      return parts.length === 1 ? { page: first } : { page: 'not-found' };
    case 'calendar':
    case 'files':
      return { page: 'module', module: first, rest: parts.slice(1) };
    case 'home':
    case 'direct': {
      if (!second) return { page: first };
      const fixed = first === 'home' ? ['create', 'search', 'saved', 'mentions', 'invites'] : ['create'];
      if (fixed.includes(second)) return third ? { page: 'not-found' } : ({ page: `${first}-${second}` } as Route);
      if (!room(second) || fourth) return { page: 'not-found' };
      return { page: `${first}-room`, room: second, event: third };
    }
  }
  if (!room(first)) return { page: 'not-found' };
  if (!second) return { page: 'space', space: first };
  if ((second === 'lobby' || second === 'search') && !third) return { page: `space-${second}`, space: first };
  if (!room(second) || fourth || more.length) return { page: 'not-found' };
  return { page: 'space-room', space: first, room: second, event: third };
}

const enc = encodeURIComponent;

/** The path of a room: in a space, a direct chat or Home (05, "Where a channel opens"). */
export function roomPath(where: { space?: string; direct?: boolean }, room: string, event?: string): string {
  const base = where.space ? `/${enc(where.space)}/` : where.direct ? '/direct/' : '/home/';
  return `${base}${enc(room)}/${event ? `${enc(event)}/` : ''}`;
}

/** desyr's own link to a channel or message: `<origin>/to/#/<channel>[/<message>][?via=…]`. */
export function linkTo(room: string, event?: string, via: string[] = []): string {
  const query = via.length ? `?${via.map((v) => `via=${enc(v)}`).join('&')}` : '';
  return `${location.origin}/to/#/${enc(room)}${event ? `/${enc(event)}` : ''}${query}`;
}

export type Link =
  | { kind: 'user'; userId: string }
  | { kind: 'room'; room: string; event?: string; via: string[] };

/**
 * Reads a link a person follows: desyr's own `/to/#/` links to channels and messages, and
 * matrix.to links to people. (matrix.to links to channels were a candidate to drop.)
 */
export function parseLink(href: string): Link | undefined {
  let url: URL;
  try {
    url = new URL(href, location.origin);
  } catch {
    return undefined;
  }
  const own = url.origin === location.origin && url.pathname.replace(/\/+$/, '') === '/to';
  const people = url.hostname === 'matrix.to';
  if (!own && !people) return undefined;
  const [path, query = ''] = url.hash.replace(/^#\/?/, '').split('?');
  const [first, second, ...rest] = path.split('/').map(decode);
  if (!first || rest.length) return undefined;
  if (people) return /^@[^:]+:.+$/.test(first) && !second ? { kind: 'user', userId: first } : undefined;
  if (!isRoomRef(first)) return undefined;
  const via = new URLSearchParams(query).getAll('via');
  return { kind: 'room', room: first, event: second || undefined, via };
}

// The page to open after signing in (04, "After signing in"; 05, Routes).
const RETURN = 'desyr_after_sign_in';

export const rememberReturn = (path: string) => writeJson(RETURN, path);
/**
 * Back from desyr ID's sign-in at a signed-in page (the automatic jump's, 36): it stays the page to open
 * once the circles are open (04 step 3); back at a sign-in page, the one remembered before is kept.
 */
export const keepSignedInPage = (path: string) => !SIGNED_OUT_PAGES.includes(parseRoute(path.split(/[?#]/)[0]).page) && rememberReturn(path);

/** Read once: only a path of this site counts (`//host/…` and `/\host/…` are refused). */
export function takeReturn(): string {
  const path = readJson<string>(RETURN);
  writeJson(RETURN, undefined);
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return '/home/';
  try {
    return new URL(path, location.origin).origin === location.origin ? path : '/home/';
  } catch {
    return '/home/';
  }
}

/** Signed out: an address with the shape of a signed-in page (one to three parts) is remembered. */
export const looksSignedIn = (pathname: string) => {
  const n = pathname.split('/').filter(Boolean).length;
  return n >= 1 && n <= 3;
};
